import { inject, Injectable } from '@angular/core';
import { FfdComponentStore, localStorageKey } from '@libs/utils';
import { VideoPlayerOptions } from '@libs/video-player';
import { Store } from '@ngrx/store';
import { selectCurrentUserProfile } from '@libs/store';

export enum TutorialStep {
  ExamOrder = 'exam-order',
  TeethProgression = 'teeth-progression',
  VoiceControls = 'voice-controls',
  InputMode = 'input-mode',
}

export interface TutorialStepContent {
  titleKey: string;
  descriptionKey: string;
  video?: VideoPlayerOptions;
  imagePath?: string;
}

export interface TutorialState {
  activeStep: TutorialStep;
  hidePerioExamTutorial: boolean;
  userId: string | undefined;
}

export interface TutorialVM {
  availableTabs: TutorialStep[];
  siteId?: string;
  activeStep: TutorialStep;
  lastStep: boolean;
  firstStep: boolean;
  hidePerioExamTutorial: boolean;
}

export const stepOrderRecord: Record<TutorialStep, TutorialStepContent> = {
  [TutorialStep.ExamOrder]: {
    titleKey: 'perio_exam_tutorial_step_exam_order_title',
    descriptionKey: 'perio_exam_tutorial_step_exam_order_description',
    imagePath: 'assets/images/perio-tutorial-slide-1.png',
  },
  [TutorialStep.TeethProgression]: {
    titleKey: 'perio_exam_tutorial_step_teeth_progression_title',
    descriptionKey: 'perio_exam_tutorial_step_teeth_progression_description',
    imagePath: 'assets/images/perio-tutorial-slide-2.png',
  },
  [TutorialStep.VoiceControls]: {
    titleKey: 'perio_exam_tutorial_step_voice_controls_title',
    descriptionKey: 'perio_exam_tutorial_step_voice_controls_description',
    imagePath: 'assets/images/perio-tutorial-slide-3.png',
  },
  [TutorialStep.InputMode]: {
    titleKey: 'perio_exam_tutorial_step_input_mode_title',
    descriptionKey: 'perio_exam_tutorial_step_input_mode_description',
    imagePath: 'assets/images/perio-tutorial-slide-4.png',
  },
};

@Injectable({ providedIn: 'root' })
export class PerioExamTutorialService extends FfdComponentStore<TutorialState, TutorialVM> {
  private readonly store = inject(Store);
  public constructor() {
    super({
      activeStep: TutorialStep.ExamOrder,
      hidePerioExamTutorial: false,
      userId: undefined,
    });
  }

  public override init(): void {
    const currentUserId = this.store.selectSignal(selectCurrentUserProfile)()?.id;
    this.setUserId(currentUserId);
    const hidePerioExamTutorial = !!JSON.parse(
      localStorage.getItem(`${localStorageKey.hidePerioExamTutorial}_${currentUserId}`) || 'false',
    );

    if (hidePerioExamTutorial !== undefined) {
      localStorage.setItem(`${localStorageKey.hidePerioExamTutorial}_${currentUserId}`, 'false');
    }
    this.setHidePerioExamTutorial(hidePerioExamTutorial);
  }

  public readonly setUserId = this.updater(
    (state, providerId: string | undefined): TutorialState => ({
      ...state,
      userId: providerId,
    }),
  );

  public readonly setActiveStep = this.updater(
    (state, activeStep: TutorialStep): TutorialState => ({
      ...state,
      activeStep,
    }),
  );

  public readonly setHidePerioExamTutorial = this.updater((state, hide: boolean): TutorialState => {
    localStorage.setItem(`${localStorageKey.hidePerioExamTutorial}_${state.userId}`, String(hide));
    return {
      ...state,
      hidePerioExamTutorial: hide,
    };
  });

  public override mapStateToViewModel(state: TutorialState): TutorialVM {
    const availableTabs = Object.values(TutorialStep);
    const activeStep = state.activeStep;
    const lastStep = availableTabs.indexOf(state.activeStep) === availableTabs.length - 1;
    const firstStep = availableTabs.indexOf(state.activeStep) === 0;
    const hidePerioExamTutorial = state.hidePerioExamTutorial;

    return {
      availableTabs,
      activeStep,
      lastStep,
      firstStep,
      hidePerioExamTutorial,
    };
  }
}
